import { useState, useEffect } from 'react';
import { 
  Package, MapPin, Calendar, TrendingDown, Users, 
  AlertCircle, Clock, DollarSign, Truck, Send, X, Phone, MessageCircle 
} from 'lucide-react';
import { BidRequest, Bid, BID_REQUEST_STATUS_CONFIG } from '../types/bidding';
import { useAuth } from '../contexts/AuthContext';
import { bidService } from '../services/bidService';
import { driverService } from '../services/driverService';
import { vehicleService } from '../services/vehicleService';
import { hasUserRole } from '../utils/userRoles';
import { isCorporateExternalTransportMobileUser } from '../utils/corporateMobileUser';
import { toast } from 'sonner';

export function BidMarketplace() {
  const { user } = useAuth();
  const [bidRequests, setBidRequests] = useState<any[]>([]);
  const [myBids, setMyBids] = useState<any[]>([]);
  const [selectedRequest, setSelectedRequest] = useState<any | null>(null);
  const [showBidModal, setShowBidModal] = useState(false);
  const [bidAmount, setBidAmount] = useState('');
  const [vehiclePlate, setVehiclePlate] = useState('');
  const [vehicleType, setVehicleType] = useState('');
  const [trailerPlate, setTrailerPlate] = useState('');
  const [notes, setNotes] = useState('');
  const [companyDrivers, setCompanyDrivers] = useState<any[]>([]);
  const [companyVehicles, setCompanyVehicles] = useState<any[]>([]);
  const [selectedCompanyDriverId, setSelectedCompanyDriverId] = useState('');
  const [selectedCompanyVehicleId, setSelectedCompanyVehicleId] = useState('');
  const [filter, setFilter] = useState<'all' | 'open' | 'my-bids'>('open');
  const [loading, setLoading] = useState(true);
  const isCorporateCarrierUser = hasUserRole(user, 'corporate-external-transport', 'corporate-external-transport-user') || isCorporateExternalTransportMobileUser(user);

  const minimumAllowedBid = selectedRequest
    ? Number(selectedRequest.minimumBid ?? selectedRequest.minimum_bid ?? 0)
    : 0;
  const currentLowestBid = selectedRequest
    ? Number(selectedRequest.currentLowestBid ?? selectedRequest.current_lowest_bid ?? 0)
    : 0;
  const parsedBidAmount = bidAmount ? parseFloat(bidAmount) : 0;
  const bidAmountError = !bidAmount
    ? ''
    : minimumAllowedBid > 0 && parsedBidAmount < minimumAllowedBid
      ? `Minimum teklif bedeli ${minimumAllowedBid.toLocaleString('tr-TR')} ₺ altinda olamaz.`
      : currentLowestBid > 0 && parsedBidAmount >= currentLowestBid
        ? `Teklifiniz mevcut en dusuk teklif olan ${currentLowestBid.toLocaleString('tr-TR')} ₺ tutarinin altinda olmalidir.`
        : '';

  const isTransferDateValid = (bid: any) => {
    const transferDate = bid.planned_loading_time || bid.pickup_date || bid.created_at;
    if (!transferDate) return true;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const parsedDate = new Date(transferDate);
    if (Number.isNaN(parsedDate.getTime())) return true;
    parsedDate.setHours(0, 0, 0, 0);
    return parsedDate >= today;
  };

  useEffect(() => {
    if (user) {
      loadBidRequests();
      loadMyBids();
      loadCompanyDrivers();
    }
  }, [user]);

  const loadCompanyDrivers = async () => {
    if (!user || !isCorporateCarrierUser) {
      setCompanyDrivers([]);
      return;
    }
    try {
      const companyId = String((user as any)?.externalTransportAccountUserId || (user as any)?.external_transport_account_user_id || user?.id || '');
      const [response, vehiclesResponse]: any[] = await Promise.all([
        driverService.getApplications({ status: 'approved', all: true }),
        vehicleService.getVehicles({ ownerType: 'external', externalTransportAccountUserId: companyId }).catch(() => ({ data: [] })),
      ]);
      const rows = Array.isArray(response?.data) ? response.data : [];
      setCompanyDrivers(rows.filter((item: any) => String(item.external_transport_account_user_id || '') === companyId && item.driver_id));
      setCompanyVehicles(Array.isArray(vehiclesResponse?.data) ? vehiclesResponse.data : []);
    } catch (error) {
      console.error('Error loading company drivers:', error);
      setCompanyDrivers([]);
      setCompanyVehicles([]);
    }
  };

  const normalizePlate = (plate: any) => String(plate || '').replace(/\s+/g, '').toLocaleUpperCase('tr-TR');

  const selectCompanyVehicle = (vehicleId: string) => {
    const vehicle = companyVehicles.find((item: any) => String(item.id || '') === String(vehicleId));
    setSelectedCompanyVehicleId(vehicleId);
    setVehiclePlate(vehicle?.cekici_plaka || vehicle?.plate || '');
    setTrailerPlate(vehicle?.dorse_plaka || vehicle?.trailer_plate || '');
    setVehicleType(vehicle?.vehicle_type || '');
  };

  const selectCompanyDriver = (driverId: string) => {
    const selected = companyDrivers.find((item: any) => String(item.driver_id || '') === String(driverId));
    setSelectedCompanyDriverId(driverId);
    const matchedVehicle = companyVehicles.find((vehicle: any) => {
      const vehicleTractor = normalizePlate(vehicle.cekici_plaka || vehicle.plate);
      const driverTractor = normalizePlate(selected?.cekici_plaka);
      return vehicleTractor && driverTractor && vehicleTractor === driverTractor;
    });
    if (matchedVehicle) {
      selectCompanyVehicle(String(matchedVehicle.id || ''));
      return;
    }
    setSelectedCompanyVehicleId('');
    setVehiclePlate(selected?.cekici_plaka || '');
    setTrailerPlate(selected?.dorse_plaka || '');
    setVehicleType(selected?.vehicle_type || '');
  };

  const loadBidRequests = async () => {
    try {
      setLoading(true);
      const response = await bidService.getBidRequests({ status: 'open' });
      if (response.success && response.data) {
        setBidRequests((response.data || []).filter((bid: any) => isTransferDateValid(bid)));
      }
    } catch (error) {
      console.error('Error loading bid requests:', error);
      toast.error('Teklif talepleri yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const loadMyBids = async () => {
    if (!user) return;
    try {
      const response = isCorporateCarrierUser
        ? await bidService.getAll({ status: 'pending' })
        : await bidService.getAll({ driverId: user.id, status: 'pending' });
      if (response.success && response.data) {
        const rows = Array.isArray(response.data) ? response.data : [];
        const companyId = String((user as any)?.externalTransportAccountUserId || (user as any)?.external_transport_account_user_id || user?.id || '');
        setMyBids(isCorporateCarrierUser ? rows.filter((item: any) => String(item.external_transport_account_user_id || '') === companyId) : rows);
      }
    } catch (error) {
      console.error('Error loading my bids:', error);
    }
  };

  const handleSubmitBid = async () => {
    if (!selectedRequest || !bidAmount || !user) return;

    const amount = parseFloat(bidAmount);

    // Minimum kontrol
    if (minimumAllowedBid > 0 && amount < minimumAllowedBid) {
      toast.error(`Minimum teklif bedeli: ${minimumAllowedBid.toLocaleString('tr-TR')} ₺`);
      return;
    }

    // En düşük teklifin altına teklif veremez
    if (currentLowestBid > 0 && amount >= currentLowestBid) {
      toast.error(`Teklifiniz su anki en dusuk tekliften (${currentLowestBid.toLocaleString('tr-TR')} ₺) daha dusuk olmalidir!`);
      return;
    }

    try {
      const selectedCompanyDriver = isCorporateCarrierUser
        ? companyDrivers.find((item: any) => String(item.driver_id || '') === selectedCompanyDriverId)
        : null;

      if (isCorporateCarrierUser && !selectedCompanyDriver) {
        toast.error('Lütfen firma sürücüsü seçin');
        return;
      }
      const selectedCompanyVehicle = isCorporateCarrierUser
        ? companyVehicles.find((item: any) => String(item.id || '') === selectedCompanyVehicleId)
        : null;

      if (isCorporateCarrierUser && !selectedCompanyVehicle) {
        toast.error('Lütfen firma çekicisi seçin');
        return;
      }

      const response = await bidService.create({
        shipmentId: selectedRequest.shipment_id,
        driverId: isCorporateCarrierUser ? String(selectedCompanyDriver.driver_id) : user.id,
        bidAmount: amount,
        notes: notes || undefined,
        tractorPlate: isCorporateCarrierUser ? (selectedCompanyVehicle?.cekici_plaka || selectedCompanyVehicle?.plate || vehiclePlate || undefined) : undefined,
        trailerPlate: isCorporateCarrierUser ? (selectedCompanyVehicle?.dorse_plaka || selectedCompanyVehicle?.trailer_plate || trailerPlate || undefined) : undefined,
        vehicleType: isCorporateCarrierUser ? (selectedCompanyVehicle?.vehicle_type || vehicleType || undefined) : undefined,
        externalTransportAccountUserId: isCorporateCarrierUser ? String((user as any)?.externalTransportAccountUserId || (user as any)?.external_transport_account_user_id || user?.id || '') : undefined,
        externalTransportActorUserId: isCorporateCarrierUser ? String(user?.id || '') : undefined,
      });

      if (response.success) {
        toast.success('Teklifiniz başarıyla gönderildi!');
        setShowBidModal(false);
        resetBidForm();
        loadBidRequests();
        loadMyBids();
      } else {
        toast.error(response.message || 'Teklif gönderilemedi');
      }
    } catch (error) {
      console.error('Error submitting bid:', error);
      toast.error('Teklif gönderilirken hata oluştu');
    }
  };

  const resetBidForm = () => {
    setBidAmount('');
    setVehiclePlate('');
    setTrailerPlate('');
    setVehicleType('');
    setNotes('');
    setSelectedCompanyDriverId('');
    setSelectedCompanyVehicleId('');
    setSelectedRequest(null);
  };

  const openBidModal = (request: BidRequest) => {
    // Daha önce teklif verilmiş mi kontrol et
    const existingBid = myBids.find(b => b.bidRequestId === request.id);
    if (existingBid) {
      alert('Bu sevkiyat için zaten teklif verdiniz!');
      return;
    }

    setSelectedRequest(request);
    setShowBidModal(true);
  };

  const hasMyBid = (requestId: string) => {
    return myBids.some(b => b.bidRequestId === requestId);
  };

  const getMyBid = (requestId: string) => {
    return myBids.find(b => b.bidRequestId === requestId);
  };

  const filteredRequests = bidRequests.filter(req => {
    if (filter === 'open') return req.status === 'open';
    if (filter === 'my-bids') return hasMyBid(req.id);
    return true;
  });

  const getTimeRemaining = (deadline: string) => {
    const now = new Date().getTime();
    const end = new Date(deadline).getTime();
    const diff = end - now;

    if (diff <= 0) return 'Süresi doldu';

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

    if (days > 0) return `${days} gün ${hours} saat`;
    return `${hours} saat`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Teklif Pazarı</h1>
        <p className="text-gray-600 mt-1">Açık sevkiyatlara teklif verin ve iş kazanın</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Açık Sevkiyatlar</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {bidRequests.filter(r => r.status === 'open').length}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <Package className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Verdiğim Teklifler</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{myBids.length}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Send className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Kazanılan</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {myBids.filter(b => b.status === 'accepted').length}
              </p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <TrendingDown className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Bekleyen</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {myBids.filter(b => b.status === 'pending').length}
              </p>
            </div>
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
              <Clock className="w-6 h-6 text-orange-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex gap-2">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === 'all'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Tümü ({bidRequests.length})
          </button>
          <button
            onClick={() => setFilter('open')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === 'open'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Açık Sevkiyatlar ({bidRequests.filter(r => r.status === 'open').length})
          </button>
          <button
            onClick={() => setFilter('my-bids')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === 'my-bids'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Teklif Verdiklerim ({myBids.length})
          </button>
        </div>
      </div>

      {/* Bid Requests Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {filteredRequests.map(request => {
          const config = BID_REQUEST_STATUS_CONFIG[request.status] || BID_REQUEST_STATUS_CONFIG.open;
          const myBid = getMyBid(request.id);
          const isDeadlinePassed = new Date(request.bidDeadline) < new Date();

          return (
            <div key={request.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              {/* Header */}
              <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-4 text-white">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-bold">{request.sevkKodu}</h3>
                  <span 
                    className="px-3 py-1 rounded-full text-xs font-semibold"
                    style={{ backgroundColor: config.bgColor, color: config.color }}
                  >
                    {config.label}
                  </span>
                </div>
                <div className="flex items-center gap-4 text-sm">
                  <div className="flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    <span>{request.bidCount} Teklif</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    <span>{getTimeRemaining(request.bidDeadline)}</span>
                  </div>
                </div>
              </div>

              <div className="p-4 space-y-4">
                {/* Route */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-blue-50 rounded-lg p-3">
                    <p className="text-xs text-blue-600 font-medium mb-1">Yükleme</p>
                    <p className="font-semibold text-blue-900 text-sm">{request.loadingCity}</p>
                    <p className="text-xs text-blue-700 mt-0.5">{request.loadingPoint}</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-3">
                    <p className="text-xs text-green-600 font-medium mb-1">Teslimat</p>
                    <p className="font-semibold text-green-900 text-sm">{request.deliveryCity}</p>
                    <p className="text-xs text-green-700 mt-0.5">{request.deliveryPoint}</p>
                  </div>
                </div>

                {/* Cargo Info */}
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="flex items-start gap-2 mb-2">
                    <Package className="w-4 h-4 text-gray-600 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">{request.cargoType}</p>
                      <div className="flex gap-3 mt-1 text-xs text-gray-600">
                        <span>{request.cargoWeight.toLocaleString()} kg</span>
                        <span>{request.cargoVolume} m³</span>
                        <span>{request.distance} km</span>
                        {request.loading_distance_km != null ? <span>Canlı Uzaklık: {Number(request.loading_distance_km).toLocaleString('tr-TR')} km</span> : null}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Dates */}
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <p className="text-gray-600 mb-1">Yükleme Tarihi</p>
                    <p className="font-medium text-gray-900">
                      {new Date(request.plannedLoadingTime).toLocaleDateString('tr-TR')}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-600 mb-1">Teslimat Tarihi</p>
                    <p className="font-medium text-gray-900">
                      {new Date(request.plannedDeliveryTime).toLocaleDateString('tr-TR')}
                    </p>
                  </div>
                </div>

                {/* Bid Info */}
                <div className="border-t border-gray-200 pt-3">
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    {request.minimumBid && (
                      <div>
                      <p className="text-xs text-gray-600 mb-1">Minimum Teklif Bedeli</p>
                        <p className="font-semibold text-gray-900">
                          {request.minimumBid.toLocaleString('tr-TR')} ₺
                        </p>
                      </div>
                    )}
                    {request.currentLowestBid && (
                      <div>
                        <p className="text-xs text-gray-600 mb-1">En Düşük Teklif</p>
                        <p className="font-semibold text-green-600 flex items-center gap-1">
                          <TrendingDown className="w-4 h-4" />
                          {request.currentLowestBid.toLocaleString('tr-TR')} ₺
                        </p>
                      </div>
                    )}
                  </div>

                  {/* My Bid */}
                  {myBid && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-blue-600 font-medium">Sizin Teklifiniz</p>
                          <p className="font-bold text-blue-900 text-lg">
                            {myBid.amount.toLocaleString('tr-TR')} ₺
                          </p>
                        </div>
                        <div 
                          className="px-3 py-1 rounded-full text-xs font-semibold"
                          style={{ 
                            backgroundColor: myBid.status === 'pending' ? '#FEF3C7' : 
                                           myBid.status === 'accepted' ? '#D1FAE5' : '#FEE2E2',
                            color: myBid.status === 'pending' ? '#F59E0B' : 
                                   myBid.status === 'accepted' ? '#10B981' : '#EF4444'
                          }}
                        >
                          {myBid.status === 'pending' ? 'Beklemede' :
                           myBid.status === 'accepted' ? 'Kazandınız!' : 'Reddedildi'}
                        </div>
                      </div>
                      {myBid.status === 'accepted' && (
                        <div className="mt-2 flex items-center gap-1 text-xs text-green-700">
                          <AlertCircle className="w-3 h-3" />
                          <span>Tebrikler! Bu işi kazandınız. "Aktif İşlerim" bölümünden takip edebilirsiniz.</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Action Button */}
                  {request.status === 'open' && !myBid && !isDeadlinePassed && (
                    <button
                      onClick={() => openBidModal(request)}
                      className="w-full bg-blue-600 text-white py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                    >
                      <Send className="w-4 h-4" />
                      Teklif Ver
                    </button>
                  )}

                  {isDeadlinePassed && request.status === 'open' && (
                    <div className="bg-gray-100 text-gray-600 text-center py-2 rounded-lg text-sm">
                      Teklif süresi doldu
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {filteredRequests.length === 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Sonuç Bulunamadı</h3>
          <p className="text-gray-600">Seçili filtreye göre gösterilecek sevkiyat bulunmuyor.</p>
        </div>
      )}

      {/* Bid Modal */}
      {showBidModal && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-lg w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900">Teklif Ver</h3>
              <button
                onClick={() => {
                  setShowBidModal(false);
                  resetBidForm();
                }}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Request Summary */}
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <p className="font-semibold text-gray-900 mb-2">{selectedRequest.sevkKodu}</p>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <MapPin className="w-4 h-4" />
                <span>{selectedRequest.loadingCity} → {selectedRequest.deliveryCity}</span>
                <span className="text-gray-400">•</span>
                <span>{selectedRequest.distance} km</span>
              </div>
            </div>

            {/* Bid Info */}
            {!(selectedRequest.phone_only_quote || selectedRequest.phoneOnlyQuote) && <div className="grid grid-cols-2 gap-3 mb-4">
              {minimumAllowedBid > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-xs text-red-600 font-medium mb-1">Minimum Teklif Bedeli</p>
                  <p className="font-bold text-red-900">
                    {minimumAllowedBid.toLocaleString('tr-TR')} ₺
                  </p>
                </div>
              )}
              {currentLowestBid > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-xs text-yellow-600 font-medium mb-1">En Düşük Teklif</p>
                  <p className="font-bold text-yellow-900 flex items-center gap-1">
                    <TrendingDown className="w-4 h-4" />
                    {currentLowestBid.toLocaleString('tr-TR')} ₺
                  </p>
                </div>
              )}
            </div>}

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-blue-600 mt-0.5" />
                <div className="text-xs text-blue-700">
                  <p className="font-medium">Önemli Bilgi:</p>
                  {selectedRequest.phone_only_quote || selectedRequest.phoneOnlyQuote ? <p className="mt-1">Bu talepte teklif tutarı uygulama içinden girilmez. Sevkiyat irtibat numarasını kullanarak arama veya WhatsApp üzerinden teklif iletmeniz gerekir.</p> : <p className="mt-1">
                     Minimum teklif bedeli {minimumAllowedBid > 0 ? `${minimumAllowedBid.toLocaleString('tr-TR')} ₺` : 'tanimli degil'}.
                     {currentLowestBid > 0 && (
                       <span> Teklifiniz ayrica mevcut en dusuk teklif olan {currentLowestBid.toLocaleString('tr-TR')} ₺ tutarinin altinda olmalidir.</span>
                     )}
                    </p>}
                  </div>
                </div>
            </div>

            {(selectedRequest.shipment_contact_phone || selectedRequest.shipmentContactPhone) && !(selectedRequest.phone_only_quote || selectedRequest.phoneOnlyQuote) && (
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mb-4">
                <div className="flex items-start gap-2">
                  <Phone className="w-4 h-4 text-slate-600 mt-0.5" />
                  <div className="text-xs text-slate-700 w-full">
                    <p className="font-medium">Sevkiyat İrtibat No</p>
                    <p className="mt-1">Teklif vermeden önce bu sevkiyatla ilgili iletişime geçebilirsiniz.</p>
                    <div className="mt-3 grid grid-cols-2 gap-2">
                      <a href={`tel:${selectedRequest.shipment_contact_phone || selectedRequest.shipmentContactPhone}`} className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 py-2 text-white font-medium hover:bg-blue-700"><Phone className="w-4 h-4" />Ara</a>
                      <a href={`https://wa.me/${String(selectedRequest.shipment_contact_phone || selectedRequest.shipmentContactPhone).replace(/\D/g, '')}?text=${encodeURIComponent(`${selectedRequest.sevkKodu} numaralı sevkiyat ile ilgili görüşecektim.`)}`} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2 rounded-lg bg-green-600 py-2 text-white font-medium hover:bg-green-700"><MessageCircle className="w-4 h-4" />WhatsApp</a>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Form */}
            {(selectedRequest.phone_only_quote || selectedRequest.phoneOnlyQuote) ? (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <a
                    href={selectedRequest.shipment_contact_phone || selectedRequest.shipmentContactPhone || selectedRequest.loading_contact_phone || selectedRequest.loadingContactPhone ? `tel:${selectedRequest.shipment_contact_phone || selectedRequest.shipmentContactPhone || selectedRequest.loading_contact_phone || selectedRequest.loadingContactPhone}` : '#'}
                    className={`inline-flex items-center justify-center gap-2 rounded-lg py-3 font-medium ${selectedRequest.shipment_contact_phone || selectedRequest.shipmentContactPhone || selectedRequest.loading_contact_phone || selectedRequest.loadingContactPhone ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-200 text-gray-500 pointer-events-none'}`}
                  >
                    <Phone className="w-4 h-4" /> Teklif İçin Ara
                  </a>
                  <a
                    href={selectedRequest.shipment_contact_phone || selectedRequest.shipmentContactPhone || selectedRequest.loading_contact_phone || selectedRequest.loadingContactPhone ? `https://wa.me/${String(selectedRequest.shipment_contact_phone || selectedRequest.shipmentContactPhone || selectedRequest.loading_contact_phone || selectedRequest.loadingContactPhone).replace(/\D/g, '')}?text=${encodeURIComponent(`${selectedRequest.sevkKodu} numaralı sevkiyat ile ilgili görüşecektim.`)}` : '#'}
                    target="_blank"
                    rel="noreferrer"
                    className={`inline-flex items-center justify-center gap-2 rounded-lg py-3 font-medium ${selectedRequest.shipment_contact_phone || selectedRequest.shipmentContactPhone || selectedRequest.loading_contact_phone || selectedRequest.loadingContactPhone ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-gray-200 text-gray-500 pointer-events-none'}`}
                  >
                    <MessageCircle className="w-4 h-4" /> Mesajla Teklif Ver
                  </a>
                </div>
                {!(selectedRequest.shipment_contact_phone || selectedRequest.shipmentContactPhone || selectedRequest.loading_contact_phone || selectedRequest.loadingContactPhone) && <p className="text-xs text-red-600">Sevkiyat için irtibat numarası tanımlı değil.</p>}
              </div>
            ) : <div className="space-y-4">
              {isCorporateCarrierUser && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Firma Sürücüsü *</label>
                  <select
                    value={selectedCompanyDriverId}
                    onChange={(e) => selectCompanyDriver(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Sürücü seçin</option>
                    {companyDrivers.map((driver: any) => (
                      <option key={driver.id || driver.driver_id} value={String(driver.driver_id || '')}>
                        {(driver.full_name || driver.name || '-') + (driver.phone ? ` • ${driver.phone}` : '') + (driver.cekici_plaka ? ` • ${driver.cekici_plaka}` : '')}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {isCorporateCarrierUser && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Firma Çekicisi *</label>
                  <select
                    value={selectedCompanyVehicleId}
                    onChange={(e) => selectCompanyVehicle(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Çekici seçin</option>
                    {companyVehicles.map((vehicle: any) => (
                      <option key={vehicle.id} value={String(vehicle.id || '')}>
                        {(vehicle.cekici_plaka || vehicle.plate || '-') + ' / ' + (vehicle.dorse_plaka || vehicle.trailer_plate || '-') + (vehicle.vehicle_type ? ` • ${vehicle.vehicle_type}` : '')}
                      </option>
                    ))}
                  </select>
                  {selectedCompanyDriverId && !selectedCompanyVehicleId ? <p className="mt-2 text-xs text-amber-700">Seçilen sürücüye bağlı çekici firma listesinde bulunamadı. Lütfen çekici seçin.</p> : null}
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Teklif Tutarı (₺) *
                </label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                   <input
                     type="number"
                     value={bidAmount}
                     onChange={(e) => setBidAmount(e.target.value)}
                     className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${bidAmountError ? 'border-red-400 bg-red-50' : 'border-gray-300'}`}
                     placeholder="Teklif tutarinizi girin"
                     min={minimumAllowedBid > 0 ? minimumAllowedBid : 0}
                     step="0.01"
                   />
                 </div>
                 {bidAmountError ? (
                   <p className="mt-2 text-xs text-red-600">{bidAmountError}</p>
                 ) : minimumAllowedBid > 0 ? (
                   <p className="mt-2 text-xs text-gray-500">Minimum teklif bedeli: {minimumAllowedBid.toLocaleString('tr-TR')} ₺</p>
                 ) : null}
               </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Araç Plakası {isCorporateCarrierUser ? '' : '(Opsiyonel)'}
                </label>
                <div className="relative">
                  <Truck className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={vehiclePlate}
                    onChange={(e) => setVehiclePlate(e.target.value)}
                    readOnly={isCorporateCarrierUser}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Örn: 34 ABC 123"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Dorse Plakası {isCorporateCarrierUser ? '' : '(Opsiyonel)'}
                </label>
                <div className="relative">
                  <Truck className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={trailerPlate}
                    onChange={(e) => setTrailerPlate(e.target.value)}
                    readOnly={isCorporateCarrierUser}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Örn: 34 XYZ 456"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Araç Tipi {isCorporateCarrierUser ? '' : '(Opsiyonel)'}
                </label>
                <select
                  value={vehicleType}
                  onChange={(e) => setVehicleType(e.target.value)}
                  disabled={isCorporateCarrierUser}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Seçiniz</option>
                  <option value="TIR">TIR</option>
                  <option value="Kamyon">Kamyon</option>
                  <option value="Kamyonet">Kamyonet</option>
                  <option value="Frigo">Frigo</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notlar (Opsiyonel)
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={3}
                  placeholder="Ek bilgi veya özel koşullarınız..."
                />
              </div>
            </div>}

            {/* Actions */}
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowBidModal(false);
                  resetBidForm();
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                İptal
              </button>
              {!(selectedRequest.phone_only_quote || selectedRequest.phoneOnlyQuote) && <button
                 onClick={handleSubmitBid}
                  disabled={!bidAmount || Boolean(bidAmountError) || (isCorporateCarrierUser && (!selectedCompanyDriverId || !selectedCompanyVehicleId))}
                 className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
               >
                <Send className="w-4 h-4" />
                Teklif Gönder
              </button>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
